import {
  type CustomRouteInput,
  DECISION_LABELS,
  PRIORITIES,
  PRIORITY_LABELS,
  TASK_KIND_LABELS,
  type CommentDto,
  type Decision,
  type LaunchCheckResultDto,
  type PreviewDto,
  type Priority,
  type RequestTypeDto,
  type RouteDto,
  type StageDto,
  type StepDto,
  type SubtaskDto,
  type TaskDetailDto,
  shownFields,
} from '@edms/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import { CUSTOM_ROUTE, CustomRouteEditor, customRouteProblem, emptyCustomRoute } from '../components/custom-route';
import { FieldsForm, FieldsView, type Values } from '../components/fields';
import { FilesBlock } from '../components/files';
import { PointerArrow } from '../components/pointer';
import { RichText, RichTextEditor } from '../components/rich-text';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Icon } from '../lib/icons';
import { DOC_DUE_HINT, Due, fromLocalInput, PriorityMark, StatusBadge, StepBadge, Ticks, toLocalInput, UserSelect, useRouteOptions } from '../lib/tasks-ui';
import { Avatar, Empty, formatDate, useToast } from '../lib/ui';

/** Действие над задачей: после успеха обновляет карточку, списки и уведомления. */
function useTaskAction<T>(id: string, fn: (arg: T) => Promise<unknown>, done?: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['task', id] });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['notifications'] });
      if (done) toast(done);
    },
    onError: (e) => toast(e.message, { error: true }),
  });
}

type CheckTarget = NonNullable<LaunchCheckResultDto['target']>;
const targetKey = (x: CheckTarget) => (x.type === 'file' ? `file:${x.kind}` : `field:${x.field}`);
/** Где на странице исправлять: подсказка в «Файлах» или поле заявки. */
const targetSelector = (x: CheckTarget) => (x.type === 'file' ? `[data-check-target="file:${x.kind}"]` : `.task-main [data-field="${CSS.escape(x.field)}"]`);

export function TaskPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const focus = params.get('sub');
  // Проверки перед запуском (из блока «Кто будет согласовывать») и стрелка к месту ошибки.
  const [checks, setChecks] = useState<LaunchCheckResultDto[]>([]);
  const [pointer, setPointer] = useState<{ key: string; nonce: number } | null>(null);
  const onChecks = (next: LaunchCheckResultDto[]) => {
    setChecks(next);
    // Ошибку исправили — стрелка больше не нужна.
    setPointer((p) => (p && next.some((c) => !c.ok && c.target && targetKey(c.target) === p.key) ? p : null));
  };
  const task = useQuery({ queryKey: ['task', id], queryFn: () => api.get<TaskDetailDto>(`/tasks/${id}`) });
  const qc = useQueryClient();
  useEffect(() => {
    // Открытие карточки отмечает прочтение — обновим счётчики.
    if (task.data) void qc.invalidateQueries({ queryKey: ['tasks', 'counts'] });
  }, [task.data?.id, qc]);

  if (task.error) {
    return <Empty emoji="🔒" title="Задача не найдена">Возможно, у вас нет к ней доступа</Empty>;
  }
  if (!task.data) return <p className="muted">Загрузка…</p>;
  const t = task.data;
  // Подзадача открывается в карточке основной задачи.
  if (t.root) return <Navigate to={`/tasks/${t.root.id}?sub=${t.id}`} replace />;
  const closed = ['approved', 'done', 'rejected', 'cancelled'].includes(t.status);
  const failing = checks.filter((c) => !c.ok);
  const pointed = pointer ? failing.find((c) => c.target && targetKey(c.target) === pointer.key) : undefined;

  return (
    <div className="task-layout">
      <div className="task-main">
        <header className="task-head">
          {t.parent && (
            <Link to={`/tasks/${t.parent.id}`} className="faint">
              ← №{t.parent.number} {t.parent.title}
            </Link>
          )}
          <div className="task-meta-line">
            <span className="faint">
              №{t.number} · {TASK_KIND_LABELS[t.kind]}
            </span>
            <StatusBadge status={t.status} />
            <PriorityMark priority={t.priority} />
            {t.regulation && (
              <Link to="/regulations" className="chip" title="Задача создана автоматически по расписанию">
                🗓️ Автозадача «{t.regulation.name}» · {t.regulation.period}
              </Link>
            )}
          </div>
          <h1>{t.title}</h1>
          <div className="task-people">
            <span>
              <span className="faint">Автор </span>
              {t.author.fullName}
            </span>
            {t.subject && t.subject.id !== t.author.id && (
              <span>
                <span className="faint">За сотрудника </span>
                {t.subject.fullName}
              </span>
            )}
            {t.assignee && (
              <span>
                <span className="faint">Исполнитель </span>
                {t.assignee.fullName}
                {t.status === 'open' && <Ticks activatedAt={t.createdAt} seenAt={t.assigneeSeenAt} />}
              </span>
            )}
            {t.kind === 'document' ? (
              t.dueAt && (
                <span title={DOC_DUE_HINT}>
                  <span className="faint">Нужен к </span>
                  <Due at={t.dueAt} done={closed} />
                </span>
              )
            ) : (
              <span>
                <span className="faint">Срок </span>
                <Due at={t.dueAt} done={closed} />
              </span>
            )}
          </div>
        </header>

        {t.can.edit ? (
          <EditBlock t={t} checks={failing} />
        ) : (
          <>
            {t.requestType && t.requestType.fields.length > 0 && (
              <div className="card card-pad">
                <FieldsView fields={t.requestType.fields} values={t.fields} labels={t.fieldLabels} behavior={t.requestType.behavior} />
              </div>
            )}
            {t.description && (
              <div className="card card-pad">
                <RichText text={t.description} />
              </div>
            )}
          </>
        )}
        {t.requestType && t.requestType.templates.length > 0 && <GenerateBar t={t} />}

        {t.can.decide && <DecisionBar t={t} stepId={t.can.decide} />}
        <MySubtasks t={t} focus={focus} />
        {/* Файлы — сразу после полей и решения: в черновике — до запуска, дальше — выше хода согласования, чтобы не пролистывать маршрут. */}
        <FilesBlock
          taskId={t.id}
          upload={['cancelled', 'rejected', 'done'].includes(t.status) ? 'none' : t.status === 'approved' ? 'scan' : 'all'}
          problems={failing}
          subtasks={t.subtasks}
        />
        {t.can.launch && <LaunchPanel t={t} onChecks={onChecks} onPoint={(x) => setPointer((p) => ({ key: targetKey(x), nonce: (p?.nonce ?? 0) + 1 }))} />}
        <AuthorActions t={t} />
        {t.can.delete && <DeleteTask t={t} />}
        {t.kind === 'assignment' && <AssignmentBlock t={t} />}
        {t.approval && <ApprovalPanel t={t} focus={focus} />}
        <SubtasksBlock t={t} focus={focus} />
        <Feed id={t.id} />
      </div>
      {pointed?.target && (
        <PointerArrow selector={targetSelector(pointed.target)} text={pointed.message ?? pointed.label} nonce={pointer!.nonce} onClose={() => setPointer(null)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Черновик
// ---------------------------------------------------------------------------

/** Формирование документов по шаблонам типа заявки (договор, приказ, смета). */
function GenerateBar({ t }: { t: TaskDetailDto }) {
  const { user, can } = useAuth();
  const generate = useTaskAction(t.id, (templateId: string) => api.post(`/tasks/${t.id}/generate`, { templateId }), 'Документ сформирован — он в файлах задачи');
  if (t.author.id !== user?.id && !can('requests.override') && !can('requests.view_all')) return null;
  return (
    <section className="card card-pad generate-bar">
      <span className="faint">Сформировать по шаблону:</span>
      {t.requestType!.templates.map((tpl) => (
        <button key={tpl.id} className="btn btn-sm" disabled={generate.isPending} onClick={() => generate.mutate(tpl.id)}>
          📄 {tpl.name}
        </button>
      ))}
      <span className="faint">Документ заполнится данными заявки; повторное формирование создаёт новую версию</span>
    </section>
  );
}

function EditBlock({ t, checks }: { t: TaskDetailDto; checks: LaunchCheckResultDto[] }) {
  const [title, setTitle] = useState(t.title);
  const [description, setDescription] = useState(t.description ?? '');
  const [priority, setPriority] = useState<Priority>(t.priority);
  const [due, setDue] = useState(toLocalInput(t.dueAt));
  const [routeId, setRouteId] = useState(t.customRoute ? CUSTOM_ROUTE : (t.routeId ?? ''));
  const [customRoute, setCustomRoute] = useState<CustomRouteInput>(() => t.customRoute ?? emptyCustomRoute());
  const custom = routeId === CUSTOM_ROUTE;
  const { user: me } = useAuth();
  const [fields, setFields] = useState<Values>(t.fields);
  const routeOptions = useRouteOptions(t.requestType?.id ?? null, t.kind === 'document' && t.status === 'draft');
  const types = useQuery({ queryKey: ['request-types'], queryFn: () => api.get<RequestTypeDto[]>('/request-types'), enabled: Boolean(t.requestType) });
  const calc = types.data?.find((x) => x.id === t.requestType?.id)?.calc ?? null;
  const routeLocked = t.requestType !== null && !t.requestType.allowRouteChoice && !t.requestType.allowCustomRoute;
  const draft = t.kind === 'document' && t.status === 'draft';
  // На доработке свой маршрут можно поправить: новый круг пойдёт по изменённому.
  const customEditable = draft ? custom : t.kind === 'document' && t.status === 'returned' && t.customRoute !== null;
  const customDirty = customEditable && JSON.stringify(customRoute) !== JSON.stringify(t.customRoute);
  const save = useMutation({
    mutationFn: () =>
      api.patch(`/tasks/${t.id}`, {
        title,
        description: description || null,
        priority,
        dueAt: fromLocalInput(due),
        ...(draft && !routeLocked ? { routeId: custom ? null : routeId || null } : {}),
        ...(customEditable ? { customRoute } : draft && t.customRoute ? { customRoute: null } : {}),
        ...(t.requestType ? { fields } : {}),
      }),
  });
  const qc = useQueryClient();
  const toast = useToast();
  const err = save.error instanceof ApiError ? save.error : null;
  const dirty =
    title !== t.title ||
    description !== (t.description ?? '') ||
    priority !== t.priority ||
    due !== toLocalInput(t.dueAt) ||
    routeId !== (t.customRoute ? CUSTOM_ROUTE : (t.routeId ?? '')) ||
    customDirty ||
    JSON.stringify(fields) !== JSON.stringify(t.fields);
  // Ошибка проверки запуска — у самого поля, пока в нём сохранённое значение (выбрали другое — проверится после сохранения).
  const checkErrors = Object.fromEntries(
    checks.flatMap((c) => (c.target?.type === 'field' && (fields[c.target.field] ?? null) === (t.fields[c.target.field] ?? null) ? [[`fields.${c.target.field}`, c.message ?? c.label]] : [])),
  );

  return (
    <form
      className="card card-pad form-grid"
      onSubmit={(e) => {
        e.preventDefault();
        if (customEditable && customRouteProblem(customRoute)) return;
        save.mutate(undefined, {
          onSuccess: () => {
            toast('Сохранено');
            void qc.invalidateQueries({ queryKey: ['task', t.id] });
          },
        });
      }}
    >
      {err && !Object.keys(err.fields).length && <div className="alert">{err.message}</div>}
      {t.status === 'returned' && (
        <div className="alert warn">Документ не согласован. Доработайте его и перезапустите согласование — участвовать будут все заново.</div>
      )}
      <label className="field">
        <span>Название</span>
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
      </label>
      {t.requestType && t.requestType.fields.length > 0 && (
        <FieldsForm fields={t.requestType.fields.filter((f) => !f.byApprover)} values={fields} onChange={setFields} errors={{ ...checkErrors, ...err?.fields }} behavior={t.requestType.behavior} calc={calc} taskId={t.id} requestTypeId={t.requestType.id} />
      )}
      <div className="field">
        <span>{t.requestType && t.requestType.fields.length > 0 ? 'Комментарий' : 'Описание'}</span>
        <RichTextEditor value={description} onChange={setDescription} />
      </div>
      <div className="two-cols">
        <label className="field">
          <span>Приоритет</span>
          <select className="select" value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABELS[p]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t.kind === 'document' ? 'Нужен к (необязательно)' : 'Срок'}</span>
          <input className="input" type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} />
          {t.kind === 'document' && <span className="faint">{DOC_DUE_HINT}</span>}
        </label>
      </div>
      {t.kind === 'document' && t.status === 'draft' && (
        <label className="field">
          <span>Маршрут</span>
          <select className="select" value={routeId} onChange={(e) => setRouteId(e.target.value)} disabled={routeLocked}>
            <option value="">Выберите маршрут</option>
            {routeOptions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
            {t.requestType?.allowCustomRoute && <option value={CUSTOM_ROUTE}>Свой маршрут — выбрать участников самому</option>}
          </select>
        </label>
      )}
      {customEditable && me && (
        <div className="field">
          <span>{draft ? 'Свой маршрут — только для этого документа' : 'Свой маршрут — изменения вступят в силу при перезапуске согласования'}</span>
          <CustomRouteEditor value={customRoute} onChange={setCustomRoute} authorId={t.author.id} error={err?.fields.customRoute} />
        </div>
      )}
      {dirty && (
        <button className="btn" disabled={save.isPending} style={{ justifySelf: 'start' }}>
          Сохранить изменения
        </button>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Запуск с предпросмотром
// ---------------------------------------------------------------------------

function LaunchPanel({ t, onChecks, onPoint }: { t: TaskDetailDto; onChecks: (c: LaunchCheckResultDto[]) => void; onPoint: (target: CheckTarget) => void }) {
  const [extraSteps, setExtraSteps] = useState<Array<{ stageKey: string; userId: string }>>([]);
  const [extraObservers, setExtraObservers] = useState<string[]>([]);
  const body = { extraSteps, extraObservers };
  const preview = useQuery({
    queryKey: ['task', t.id, 'preview', t.routeId, t.customRoute, body],
    queryFn: () => api.post<PreviewDto>(`/tasks/${t.id}/preview`, body),
    enabled: Boolean(t.routeId || t.customRoute),
    retry: false,
    placeholderData: (p) => p,
  });
  const launch = useTaskAction(t.id, () => api.post(`/tasks/${t.id}/launch`, body), 'Согласование запущено');
  const checks = preview.data?.checks;
  // Ошибки проверок показываются и в своих блоках — «Файлы», поле заявки.
  useEffect(() => onChecks(checks ?? []), [checks]);
  useEffect(() => () => onChecks([]), []);

  if (!t.routeId && !t.customRoute) {
    return <div className="card card-pad muted">Выберите маршрут и сохраните — здесь появится, кто и в каком порядке будет согласовывать.</div>;
  }
  const p = preview.data;
  const used = new Set(p?.stages.flatMap((s) => s.steps.map((st) => st.user?.id ?? '')) ?? []);

  return (
    <section className="card card-pad launch">
      <div className="section-head">
        <h2>Кто будет согласовывать</h2>
        <span className="faint">Можно добавить участников и наблюдателей</span>
      </div>
      {preview.error && <div className="alert">{preview.error.message}</div>}
      {p && p.checks.length > 0 && (
        <ul className="checks">
          {p.checks.map((c) => {
            const content = (
              <>
                <span aria-hidden>{c.ok ? '✓' : '✗'}</span>
                <b>{c.label}</b>
                {c.message && <span className="faint">{c.message}</span>}
              </>
            );
            const target = !c.ok ? c.target : null;
            return (
              <li key={c.label} className={c.ok ? 'ok' : 'fail'}>
                {target ? (
                  <button type="button" className="check-link" onClick={() => onPoint(target)} title="Показать, где исправить">
                    {content}
                    <span className="check-where">показать где</span>
                  </button>
                ) : (
                  content
                )}
              </li>
            );
          })}
        </ul>
      )}
      {p && p.unresolved.length > 0 && (
        <div className="alert warn">Для некоторых шагов не удалось определить сотрудника (например, не указан руководитель). Обратитесь к администратору.</div>
      )}
      <ol className="timeline">
        {p?.stages.map((s) => (
          <li key={s.key} className={`tl-stage${s.applies ? '' : ' muted-stage'}`}>
            <div className="tl-dot" />
            <div className="tl-body">
              <div className="stage-title">
                <b>{s.name}</b>
                <span className="faint">{s.mode === 'parallel' ? 'все одновременно' : 'по очереди'}</span>
                {!s.applies && <span className="badge">не требуется по условию</span>}
              </div>
              <ul className="people">
                {s.steps.map((st, i) => (
                  <li key={`${st.key}-${i}`}>
                    {st.user ? <Avatar name={st.user.fullName} size={28} /> : <span className="avatar-empty">?</span>}
                    <span>
                      {st.user?.fullName ?? <span className="field-error">не определён</span>}
                      {st.kind === 'task' && <span className="badge badge-warn" style={{ marginLeft: 6 }}>задача</span>}
                      {st.sourceLabel && <small className="faint"> · {st.sourceLabel}</small>}
                      {st.controller && <small className="faint"> · контроль: {st.controller.fullName}</small>}
                      {st.instruction && <small className="faint"> — {st.instruction}</small>}
                      {st.deadlineHours && <small className="faint"> · {st.deadlineHours} ч</small>}
                    </span>
                    {extraSteps.some((x) => x.stageKey === s.key && x.userId === st.user?.id) && (
                      <button className="btn btn-ghost btn-sm" type="button" onClick={() => setExtraSteps((xs) => xs.filter((x) => !(x.stageKey === s.key && x.userId === st.user?.id)))}>
                        убрать
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              {s.applies && (
                <AddPerson label="+ участник" exclude={t.customRoute ? [...used, t.author.id] : [...used]} onAdd={(userId) => setExtraSteps((xs) => [...xs, { stageKey: s.key, userId }])} />
              )}
            </div>
          </li>
        ))}
      </ol>
      <div className="observers">
        <span className="faint">Наблюдатели:</span>
        {p?.observers.map((o) => (
          <span key={o.id} className="chip">
            {o.fullName}
            {extraObservers.includes(o.id) && (
              <button type="button" aria-label="Убрать" onClick={() => setExtraObservers((xs) => xs.filter((x) => x !== o.id))}>
                ×
              </button>
            )}
          </span>
        ))}
        <AddPerson label="+ наблюдатель" exclude={p?.observers.map((o) => o.id) ?? []} onAdd={(id) => setExtraObservers((xs) => [...xs, id])} />
      </div>
      <button className="btn btn-primary" disabled={launch.isPending || !p || p.unresolved.length > 0 || p.checks.some((c) => !c.ok)} onClick={() => launch.mutate(undefined)}>
        Запустить согласование
      </button>
    </section>
  );
}

function AddPerson({ label, exclude, onAdd }: { label: string; exclude: string[]; onAdd: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  if (!open) {
    return (
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(true)}>
        {label}
      </button>
    );
  }
  return (
    <span className="inline-pick">
      <UserSelect value={value} onChange={setValue} exclude={exclude} />
      <button
        type="button"
        className="btn btn-sm btn-primary"
        disabled={!value}
        onClick={() => {
          onAdd(value);
          setValue('');
          setOpen(false);
        }}
      >
        Добавить
      </button>
      <button type="button" className="btn btn-sm btn-ghost" onClick={() => setOpen(false)}>
        Отмена
      </button>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Решение и действия автора
// ---------------------------------------------------------------------------

/** Поля заявки, которые участник заполняет на своём шаге («Заполнить поля» в маршруте). */
function useStepFields(t: TaskDetailDto, step: StepDto | undefined) {
  // Скрытое условием поле заполнять не нужно; условие считаем по всей заявке, а не по полям шага.
  const defs = shownFields(t.requestType?.fields ?? [], t.fields)
    .filter((f) => step?.fillFields.includes(f.key))
    .map((f) => ({ ...f, required: true, showIf: undefined }));
  const [values, setValues] = useState<Values>(() => Object.fromEntries(defs.map((f) => [f.key, t.fields[f.key]])));
  const empty = (v: unknown) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
  const missing = defs.filter((f) => empty(values[f.key])).map((f) => f.label);
  return { defs, values, setValues, missing };
}

function StepFieldsForm({ t, f }: { t: TaskDetailDto; f: ReturnType<typeof useStepFields> & { errors?: Record<string, string> } }) {
  if (f.defs.length === 0) return null;
  return (
    <div className="step-fields">
      <span className="faint">Заполните — значения сохранятся в заявке:</span>
      <FieldsForm fields={f.defs} values={f.values} onChange={f.setValues} errors={f.errors} behavior={t.requestType!.behavior} calc={null} taskId={t.id} requestTypeId={t.requestType!.id} />
    </div>
  );
}

function DecisionBar({ t, stepId }: { t: TaskDetailDto; stepId: string }) {
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const step = t.approval?.stages.flatMap((s) => s.steps).find((s) => s.id === stepId);
  const sf = useStepFields(t, step);
  const decide = useTaskAction(t.id, (decision: Decision) =>
    api.post(`/tasks/${t.id}/steps/${stepId}/decision`, { decision, comment: comment || undefined, ...(sf.defs.length ? { fields: sf.values } : {}) }),
  'Решение принято');
  const fieldErrors = decide.error instanceof ApiError ? decide.error.fields : undefined;

  const submit = (decision: Decision) => {
    if (decision !== 'approve' && !comment.trim()) {
      setError('Напишите причину — автор увидит её в задаче');
      return;
    }
    if (decision === 'approve' && sf.missing.length) {
      setError(`Заполните: ${sf.missing.map((m) => `«${m}»`).join(', ')}`);
      return;
    }
    setError(null);
    decide.mutate(decision);
  };

  if (step?.kind === 'task') return <TaskBar t={t} stepId={stepId} />;

  return (
    <section className="card card-pad decision">
      <div className="section-head">
        <h2>Ваше решение</h2>
        {step?.dueAt && <Due at={step.dueAt} />}
      </div>
      {step?.instruction && <p className="instruction">{step.instruction}</p>}
      <StepFieldsForm t={t} f={{ ...sf, errors: fieldErrors }} />
      <textarea className="textarea" rows={3} placeholder="Комментарий (обязателен, если не согласуете)" value={comment} onChange={(e) => setComment(e.target.value)} aria-invalid={Boolean(error)} />
      {error && <span className="field-error">{error}</span>}
      <div className="decision-buttons">
        <button className="btn btn-primary" disabled={decide.isPending} onClick={() => submit('approve')}>
          ✓ {DECISION_LABELS.approve}
        </button>
        <button className="btn btn-warn" disabled={decide.isPending} onClick={() => submit('decline')} title="Голос против: этап доголосует, и задача вернётся автору">
          {DECISION_LABELS.decline}
        </button>
        <button className="btn btn-danger" disabled={decide.isPending} onClick={() => submit('reject')} title="Окончательный отказ: согласование прекратится у всех">
          {DECISION_LABELS.reject}
        </button>
      </div>
      <p className="faint">
        «Не согласовать» — этап доголосует, затем задача вернётся автору на доработку. «Отклонить» — окончательный отказ, согласование прекратится у всех.
      </p>
    </section>
  );
}

const FILE_WHAT: Record<string, string> = { scan: 'скан подписанного документа', document: 'документ', attachment: 'вложение' };

/** Шаг-задача: участник выполняет действие и отмечает «Выполнено». */
function TaskBar({ t, stepId }: { t: TaskDetailDto; stepId: string }) {
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const step = t.approval!.stages.flatMap((s) => s.steps).find((s) => s.id === stepId)!;
  const sf = useStepFields(t, step);
  const done = useTaskAction(
    t.id,
    () => api.post(`/tasks/${t.id}/steps/${stepId}/decision`, { decision: 'approve', comment: comment || undefined, ...(sf.defs.length ? { fields: sf.values } : {}) }),
    'Отмечено как выполненное',
  );
  const fieldErrors = done.error instanceof ApiError ? done.error.fields : undefined;
  const submit = () => {
    if (sf.missing.length) {
      setError(`Заполните: ${sf.missing.map((m) => `«${m}»`).join(', ')}`);
      return;
    }
    if (step.requireComment && !comment.trim()) {
      setError('Комментарий обязателен для этой задачи');
      return;
    }
    setError(null);
    done.mutate(undefined);
  };
  return (
    <section className="card card-pad decision decision-task">
      <div className="section-head">
        <h2>Нужно выполнить</h2>
        {step.dueAt && <Due at={step.dueAt} />}
      </div>
      {step.instruction && <p className="instruction">{step.instruction}</p>}
      {step.requireFile && (
        <p className="faint">
          Сначала загрузите {FILE_WHAT[step.requireFile]} в разделе «Файлы» ниже, затем отметьте выполнение.
        </p>
      )}
      {step.controller && <p className="faint">Контролирует: {step.controller.fullName}</p>}
      <StepFieldsForm t={t} f={{ ...sf, errors: fieldErrors }} />
      <textarea
        className="textarea"
        rows={2}
        placeholder={step.requireComment ? 'Комментарий (обязательно): например, регистрационный номер' : 'Комментарий (необязательно)'}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        aria-invalid={Boolean(error)}
      />
      {error && <span className="field-error">{error}</span>}
      <div className="decision-buttons">
        <button className="btn btn-primary" disabled={done.isPending} onClick={submit}>
          ✓ {step.actionLabel || 'Выполнено'}
        </button>
      </div>
    </section>
  );
}

/** Свой черновик — автору; любую задачу навсегда — администратору (право «Удаление задач и маршрутов»). */
function DeleteTask({ t }: { t: TaskDetailDto }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const draft = t.status === 'draft';
  const remove = useMutation({
    mutationFn: () => api.delete(`/tasks/${t.id}`),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ['task', t.id] });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      toast(draft ? 'Черновик удалён' : 'Задача удалена');
      navigate('/tasks', { replace: true });
    },
    onError: (e) => toast(e.message, { error: true }),
  });
  const question = draft
    ? `Удалить черновик «${t.title}»? Восстановить его будет нельзя.`
    : `Удалить задачу №${t.number} «${t.title}» навсегда?\n\nВместе с ней удалятся подзадачи, файлы, история согласования и комментарии. Отменить нельзя.`;
  return (
    <div className="delete-task">
      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} disabled={remove.isPending} onClick={() => window.confirm(question) && remove.mutate()}>
        {draft ? 'Удалить черновик' : 'Удалить навсегда'}
      </button>
    </div>
  );
}

function AuthorActions({ t }: { t: TaskDetailDto }) {
  const [comment, setComment] = useState('');
  const restart = useTaskAction(t.id, () => api.post(`/tasks/${t.id}/restart`, { comment: comment || undefined }), 'Согласование перезапущено');
  const cancel = useTaskAction(t.id, () => api.post(`/tasks/${t.id}/cancel`, { comment: comment || undefined }), t.kind === 'document' ? 'Согласование прекращено' : 'Поручение отменено');
  if (!t.can.restart && !t.can.cancel) return null;

  return (
    <section className="card card-pad author-actions">
      <input className="input" placeholder="Комментарий (необязательно)" value={comment} onChange={(e) => setComment(e.target.value)} />
      {t.can.restart && (
        <button className="btn btn-primary" disabled={restart.isPending} onClick={() => restart.mutate(undefined)}>
          ↻ Перезапустить согласование
        </button>
      )}
      {t.can.cancel && (
        <button
          className="btn btn-danger"
          disabled={cancel.isPending}
          onClick={() => {
            if (window.confirm(t.kind === 'document' ? 'Прекратить согласование у всех участников?' : 'Отменить поручение?')) cancel.mutate(undefined);
          }}
        >
          {t.kind === 'document' ? 'Прекратить у всех' : 'Отменить поручение'}
        </button>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Ход согласования
// ---------------------------------------------------------------------------

function ApprovalPanel({ t, focus }: { t: TaskDetailDto; focus: string | null }) {
  const a = t.approval!;
  const [showPast, setShowPast] = useState(false);
  return (
    <section className="card card-pad">
      <div className="section-head">
        <h2>Согласование</h2>
        <span className="faint">
          {a.routeName} · круг {a.round}
        </span>
      </div>
      <ol className="timeline">
        {a.stages.map((s) => (
          <StageItem key={s.id} t={t} stage={s} focus={focus} />
        ))}
      </ol>
      {a.observers.length > 0 && (
        <div className="observers">
          <span className="faint">Наблюдатели:</span>
          {a.observers.map((o) => (
            <span key={o.id} className="chip">
              {o.fullName}
            </span>
          ))}
        </div>
      )}
      {a.pastRounds.length > 0 && (
        <div className="past">
          <button className="btn btn-ghost btn-sm" onClick={() => setShowPast((v) => !v)}>
            {showPast ? '▾' : '▸'} История прошлых кругов ({a.pastRounds.length})
          </button>
          {showPast &&
            a.pastRounds.map((r) => (
              <div key={r.round} className="past-round">
                <b>Круг {r.round}</b>
                <ul className="people">
                  {r.steps
                    .filter((s) => s.status !== 'waiting')
                    .map((s) => (
                      <li key={s.id}>
                        <Avatar name={s.user?.fullName ?? '?'} size={24} />
                        <span>
                          {s.user?.fullName} <span className="faint">· {s.stageName}</span>
                          {s.comment && <div className="step-comment">«{s.comment}»</div>}
                        </span>
                        <StepBadge status={s.status} kind={s.kind} />
                        <span className="faint">{formatDate(s.decidedAt)}</span>
                      </li>
                    ))}
                </ul>
              </div>
            ))}
        </div>
      )}
    </section>
  );
}

function StageItem({ t, stage, focus }: { t: TaskDetailDto; stage: StageDto; focus: string | null }) {
  const canAdd = t.can.override && (stage.status === 'waiting' || stage.status === 'active');
  const add = useTaskAction(t.id, (userId: string) => api.post(`/tasks/${t.id}/stages/${stage.id}/steps`, { userId }), 'Участник добавлен');
  return (
    <li className={`tl-stage st-${stage.status}`}>
      <div className="tl-dot" />
      <div className="tl-body">
        <div className="stage-title">
          <b>{stage.name}</b>
          <span className="faint">{stage.mode === 'parallel' ? 'все одновременно' : 'по очереди'}</span>
          {stage.status === 'declined' && <span className="badge badge-warn">не согласован</span>}
          {stage.status === 'skipped' && <span className="badge">пропущен по условию</span>}
        </div>
        <ul className="steps">
          {stage.steps.map((s) => (
            <StepItem key={s.id} t={t} step={s} focus={focus} />
          ))}
        </ul>
        {canAdd && <AddPerson label="+ добавить участника" exclude={stage.steps.map((s) => s.user?.id ?? '')} onAdd={(id) => add.mutate(id)} />}
      </div>
    </li>
  );
}

function StepItem({ t, step, focus }: { t: TaskDetailDto; step: StepDto; focus: string | null }) {
  const { user } = useAuth();
  const [menu, setMenu] = useState<null | 'replace' | 'override'>(null);
  const [value, setValue] = useState('');
  const replace = useTaskAction(t.id, () => api.post(`/tasks/${t.id}/steps/${step.id}/replace`, { userId: value }), 'Согласующий заменён');
  const skip = useTaskAction(t.id, () => api.post(`/tasks/${t.id}/steps/${step.id}/skip`, {}), 'Шаг снят');
  const override = useTaskAction(t.id, (decision: Decision) => api.post(`/tasks/${t.id}/steps/${step.id}/override`, { decision, comment: value }), 'Решение принято за участника');
  const open = step.status === 'waiting' || step.status === 'pending';
  const mine = step.user?.id === user?.id;

  return (
    <li className={`step${mine ? ' mine' : ''}`}>
      <div className="step-row">
        {step.user ? <Avatar name={step.user.fullName} size={32} /> : <span className="avatar-empty">?</span>}
        <div className="step-info">
          <div>
            <b>{step.user?.fullName ?? 'не определён'}</b>
            {step.user?.position && <span className="faint"> · {step.user.position}</span>}
            {step.origin !== 'route' && <span className="faint"> · добавлен{step.origin === 'admin' ? ' администратором' : ' автором'}</span>}
          </div>
          {(step.kind === 'task' || step.sourceLabel || step.controller) && (
            <div className="step-meta">
              {step.kind === 'task' && <span className="badge badge-warn">задача{step.actionLabel ? `: ${step.actionLabel}` : ''}</span>}
              {step.sourceLabel && <span className="faint">{step.sourceLabel}</span>}
              {step.controller && <span className="faint">контроль: {step.controller.fullName}</span>}
            </div>
          )}
          <div className="step-status">
            <StepBadge status={step.status} kind={step.kind} />
            {step.status === 'pending' && <Ticks activatedAt={step.activatedAt} seenAt={step.seenAt} />}
            {step.status === 'pending' && step.dueAt && <Due at={step.dueAt} />}
            {step.decidedAt && <span className="faint">{formatDate(step.decidedAt)}</span>}
            {step.decidedBy && step.decidedBy.id !== step.user?.id && <span className="faint">за него: {step.decidedBy.fullName}</span>}
          </div>
          {step.instruction && step.status !== 'approved' && <div className="faint">{step.instruction}</div>}
          {step.comment && <div className="step-comment">«{step.comment}»</div>}
        </div>
        {t.can.override && open && (
          <div className="step-admin">
            <button className="btn btn-ghost btn-sm" onClick={() => setMenu(menu === 'replace' ? null : 'replace')}>
              Заменить
            </button>
            {step.status === 'pending' && (
              <button className="btn btn-ghost btn-sm" onClick={() => setMenu(menu === 'override' ? null : 'override')}>
                Решить за
              </button>
            )}
            <button className="btn btn-ghost btn-sm" onClick={() => window.confirm('Снять шаг? Решение этого участника больше не потребуется.') && skip.mutate(undefined)}>
              Снять
            </button>
          </div>
        )}
      </div>
      {menu === 'replace' && (
        <div className="inline-pick">
          <UserSelect value={value} onChange={setValue} exclude={step.user ? [step.user.id] : []} />
          <button className="btn btn-sm btn-primary" disabled={!value || replace.isPending} onClick={() => replace.mutate(undefined)}>
            Заменить
          </button>
        </div>
      )}
      {menu === 'override' && (
        <div className="inline-pick">
          <input className="input" placeholder="Основание (обязательно)" value={value} onChange={(e) => setValue(e.target.value)} />
          {(step.kind === 'task' ? (['approve'] as const) : (['approve', 'decline', 'reject'] as const)).map((d) => (
            <button key={d} className="btn btn-sm" disabled={!value.trim() || override.isPending} onClick={() => override.mutate(d)}>
              {step.kind === 'task' ? 'Отметить выполненной' : DECISION_LABELS[d]}
            </button>
          ))}
        </div>
      )}
      {step.subtasks.length > 0 && (
        <ul className="subtasks">
          {step.subtasks.map((s) => (
            <SubtaskItem key={s.id} rootId={t.id} s={s} focus={focus} showAnswer />
          ))}
        </ul>
      )}
      {mine && step.status === 'pending' && <SubtaskForm t={t} stepId={step.id} compact />}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Поручения и подзадачи
// ---------------------------------------------------------------------------

function AssignmentBlock({ t }: { t: TaskDetailDto }) {
  const [answer, setAnswer] = useState('');
  const [comment, setComment] = useState('');
  const send = useTaskAction(t.id, () => api.post(`/tasks/${t.id}/answer`, { answer }), 'Отправлено на проверку');
  const review = useTaskAction(t.id, (accept: boolean) => api.post(`/tasks/${t.id}/review`, { accept, comment: comment || undefined }), 'Готово');

  return (
    <>
      {t.answer && (
        <section className="card card-pad">
          <div className="section-head">
            <h2>Результат</h2>
            <span className="faint">{formatDate(t.answeredAt)}</span>
          </div>
          <div className="prose">{t.answer}</div>
        </section>
      )}
      {t.can.answer && (
        <section className="card card-pad decision">
          <h2>Ответ исполнителя</h2>
          <textarea className="textarea" rows={4} placeholder="Что сделано" value={answer} onChange={(e) => setAnswer(e.target.value)} />
          <button className="btn btn-primary" disabled={!answer.trim() || send.isPending} onClick={() => send.mutate(undefined)} style={{ justifySelf: 'start' }}>
            Отправить на проверку
          </button>
        </section>
      )}
      {t.can.review && (
        <section className="card card-pad decision">
          <h2>Проверьте результат</h2>
          <textarea className="textarea" rows={3} placeholder="Комментарий (обязателен при возврате)" value={comment} onChange={(e) => setComment(e.target.value)} />
          <div className="decision-buttons">
            <button className="btn btn-primary" disabled={review.isPending} onClick={() => review.mutate(true)}>
              ✓ Принять
            </button>
            <button className="btn btn-warn" disabled={review.isPending || !comment.trim()} onClick={() => review.mutate(false)}>
              На доработку
            </button>
          </div>
        </section>
      )}
    </>
  );
}

const SUB_DONE = ['done', 'cancelled'];

/** Подзадачи, где от пользователя ждут действия: выполнить или проверить. Как «Ваше решение». */
function MySubtasks({ t, focus }: { t: TaskDetailDto; focus: string | null }) {
  const { user } = useAuth();
  const active = t.subtasks.filter((s) => s.can.answer || s.can.review);
  // Свои завершённые подзадачи тоже видны тем, у кого нет общего блока «Подзадачи».
  const stepIds = new Set(t.approval?.stages.flatMap((st) => st.steps.map((x) => x.id)) ?? []);
  const own = t.can.manageSubtasks
    ? []
    : t.subtasks.filter(
        (s) => !active.includes(s) && (s.assignee?.id === user?.id || s.author.id === user?.id) && !(s.parentStepId && stepIds.has(s.parentStepId) && s.parentId === t.id),
      );
  return (
    <>
      {active.map((s) => (
        <SubtaskBar key={s.id} rootId={t.id} s={s} focus={focus} />
      ))}
      {own.length > 0 && (
        <section className="card card-pad">
          <h2>Ваши подзадачи</h2>
          <ul className="subtasks">
            {own.map((s) => (
              <SubtaskItem key={s.id} rootId={t.id} s={s} focus={focus} showAnswer />
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

function SubtaskBar({ rootId, s, focus }: { rootId: string; s: SubtaskDto; focus: string | null }) {
  const ref = useFocus(s.id === focus);
  return (
    <section ref={ref} id={`sub-${s.id}`} className={`card card-pad decision${s.id === focus ? ' focused' : ''}`}>
      <div className="section-head">
        <h2>{s.can.answer ? 'Подзадача для вас' : 'Подзадача для вас: проверьте результат'}</h2>
        {s.dueAt && <Due at={s.dueAt} />}
      </div>
      <SubtaskBody rootId={rootId} s={s} />
    </section>
  );
}

/** Подзадача строкой; раскрывается со всей историей и действиями. */
function SubtaskItem({ rootId, s, focus, showAnswer }: { rootId: string; s: SubtaskDto; focus: string | null; showAnswer?: boolean }) {
  // Подзадача, где от пользователя ждут действия, уже раскрыта вверху («Подзадача для вас»).
  const active = s.can.answer || s.can.review;
  const focused = s.id === focus && !active;
  const [open, setOpen] = useState(focused);
  const ref = useFocus(focused);
  const closed = SUB_DONE.includes(s.status);
  return (
    <li ref={ref} id={active ? undefined : `sub-${s.id}`} className={`subtask-item${open ? ' open' : ''}${focused ? ' focused' : ''}`}>
      <button type="button" className="subtask" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="faint">{open ? '▾' : '▸'}</span>
        <span className="subtask-title">{s.title}</span>
        <span className="faint">
          {s.author.fullName} → {s.assignee?.fullName}
        </span>
        {s.dueAt && !closed && <Due at={s.dueAt} />}
        <StatusBadge status={s.status} />
      </button>
      {!open && showAnswer && s.answer && <div className="subtask-answer">Ответ: {s.answer}</div>}
      {open && (
        <div className="subtask-body">
          <SubtaskBody rootId={rootId} s={s} actionsAbove={active} />
        </div>
      )}
    </li>
  );
}

function useFocus(on: boolean) {
  const ref = useRef<HTMLElement & HTMLLIElement>(null);
  useEffect(() => {
    if (on) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [on]);
  return ref;
}

/** Всё о подзадаче: описание, результат, история, действия исполнителя и автора. */
function SubtaskBody({ rootId, s, actionsAbove }: { rootId: string; s: SubtaskDto; actionsAbove?: boolean }) {
  const [answer, setAnswer] = useState('');
  const [comment, setComment] = useState('');
  const [note, setNote] = useState('');
  const send = useTaskAction(rootId, () => api.post(`/tasks/${s.id}/answer`, { answer }), 'Отправлено на проверку');
  const review = useTaskAction(rootId, (accept: boolean) => api.post(`/tasks/${s.id}/review`, { accept, comment: comment || undefined }), 'Готово');
  const cancel = useTaskAction(rootId, () => api.post(`/tasks/${s.id}/cancel`, {}), 'Подзадача отменена');
  const addNote = useTaskAction(rootId, () => api.post(`/tasks/${s.id}/comments`, { body: note }), 'Комментарий добавлен');
  const [showHistory, setShowHistory] = useState(false);

  return (
    <div className="subtask-content">
      <div className="task-people">
        <span>
          <span className="faint">Поставил(а) </span>
          {s.author.fullName}
        </span>
        <span>
          <span className="faint">Исполнитель </span>
          {s.assignee?.fullName}
          {s.status === 'open' && <Ticks activatedAt={s.createdAt} seenAt={s.assigneeSeenAt} />}
        </span>
        <span>
          <span className="faint">Срок </span>
          <Due at={s.dueAt} done={SUB_DONE.includes(s.status)} />
        </span>
      </div>
      {s.description && <RichText text={s.description} className="instruction" />}
      {s.answer && (
        <div className="subtask-result">
          <div className="faint">Результат · {formatDate(s.answeredAt)}</div>
          <div className="prose">{s.answer}</div>
        </div>
      )}
      {s.history.length > 0 && (
        <div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowHistory((v) => !v)}>
            {showHistory ? '▾' : '▸'} История ({s.history.length})
          </button>
          {showHistory && (
            <ul className="feed">
              {s.history.map((c) => (
                <FeedItem key={c.id} c={c} />
              ))}
            </ul>
          )}
        </div>
      )}
      {actionsAbove && <p className="faint">Ответить или проверить — в блоке «Подзадача для вас» вверху страницы.</p>}
      {s.can.answer && !actionsAbove && (
        <div className="subtask-action">
          <textarea className="textarea" rows={3} placeholder="Что сделано" value={answer} onChange={(e) => setAnswer(e.target.value)} />
          <button className="btn btn-primary" disabled={!answer.trim() || send.isPending} onClick={() => send.mutate(undefined)}>
            Отправить на проверку
          </button>
        </div>
      )}
      {s.can.review && !actionsAbove && (
        <div className="subtask-action">
          <textarea className="textarea" rows={2} placeholder="Комментарий (обязателен при возврате)" value={comment} onChange={(e) => setComment(e.target.value)} />
          <div className="decision-buttons">
            <button className="btn btn-primary" disabled={review.isPending} onClick={() => review.mutate(true)}>
              ✓ Принять
            </button>
            <button className="btn btn-warn" disabled={review.isPending || !comment.trim()} onClick={() => review.mutate(false)}>
              На доработку
            </button>
          </div>
        </div>
      )}
      {!SUB_DONE.includes(s.status) && (
        <div className="subtask-note">
          <input className="input" placeholder="Комментарий к подзадаче" value={note} onChange={(e) => setNote(e.target.value)} />
          <button
            className="btn btn-sm"
            disabled={!note.trim() || addNote.isPending}
            onClick={() => addNote.mutate(undefined, { onSuccess: () => setNote('') })}
          >
            Отправить
          </button>
          {s.can.cancel && (
            <button className="btn btn-ghost btn-sm" disabled={cancel.isPending} onClick={() => window.confirm('Отменить подзадачу?') && cancel.mutate(undefined)}>
              Отменить подзадачу
            </button>
          )}
        </div>
      )}
      {/* Файлы подзадачи видны всегда: и пока её выполняют, и после приёмки. */}
      {!actionsAbove && <FilesBlock taskId={s.id} upload={s.can.answer ? 'all' : 'none'} title="Файлы подзадачи" nested hideEmpty />}
    </div>
  );
}

/** Все подзадачи задачи — только для тех, кто вправе ставить подзадачи в любой задаче. */
function SubtasksBlock({ t, focus }: { t: TaskDetailDto; focus: string | null }) {
  const [open, setOpen] = useState(false);
  if (!t.can.manageSubtasks) return null;
  const stepOwner = new Map(t.approval?.stages.flatMap((st) => st.steps.map((x) => [x.id, x.user?.fullName ?? ''] as const)) ?? []);
  return (
    <section className="card card-pad">
      <div className="section-head">
        <h2>Подзадачи</h2>
        {t.can.createSubtask && !open && (
          <button className="btn btn-sm" onClick={() => setOpen(true)}>
            <Icon name="plus" size={16} /> Подзадача
          </button>
        )}
      </div>
      {t.subtasks.length > 0 ? (
        <ul className="subtasks">
          {t.subtasks.map((s) => (
            <Fragment key={s.id}>
              {s.parentStepId && stepOwner.has(s.parentStepId) && <li className="faint subtask-origin">по шагу согласования: {stepOwner.get(s.parentStepId)}</li>}
              <SubtaskItem rootId={t.id} s={s} focus={null} showAnswer />
            </Fragment>
          ))}
        </ul>
      ) : (
        !open && <p className="faint">Подзадач нет</p>
      )}
      {open && <SubtaskForm t={t} onDone={() => setOpen(false)} />}
    </section>
  );
}

function SubtaskForm({ t, stepId, compact, onDone }: { t: TaskDetailDto; stepId?: string; compact?: boolean; onDone?: () => void }) {
  const [expanded, setExpanded] = useState(!compact);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [due, setDue] = useState('');
  const create = useTaskAction(
    t.id,
    () => api.post('/tasks', { kind: 'assignment', title, description: description || undefined, assigneeId, parentId: t.id, parentStepId: stepId, dueAt: fromLocalInput(due) }),
    'Подзадача поставлена',
  );
  if (!expanded) {
    return (
      <button className="btn btn-ghost btn-sm" onClick={() => setExpanded(true)}>
        + Поставить подзадачу по своему шагу
      </button>
    );
  }
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate(undefined, {
      onSuccess: () => {
        setTitle('');
        setDescription('');
        setAssigneeId('');
        setDue('');
        if (compact) setExpanded(false);
        onDone?.();
      },
    });
  };
  return (
    <form className="subtask-form" onSubmit={submit}>
      <input className="input" placeholder="Что сделать" value={title} onChange={(e) => setTitle(e.target.value)} required />
      <UserSelect value={assigneeId} onChange={setAssigneeId} placeholder="Исполнитель" />
      <input className="input" type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} aria-label="Срок" />
      <div className="subtask-form-wide">
        <RichTextEditor value={description} onChange={setDescription} placeholder="Подробности (необязательно)" minHeight={60} />
      </div>
      <span style={{ display: 'flex', gap: 6 }}>
        <button className="btn btn-sm btn-primary" disabled={!title.trim() || !assigneeId || create.isPending}>
          Поставить
        </button>
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          onClick={() => {
            setExpanded(!compact);
            onDone?.();
          }}
        >
          Отмена
        </button>
      </span>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Лента
// ---------------------------------------------------------------------------

function Feed({ id }: { id: string }) {
  const qc = useQueryClient();
  const comments = useQuery({ queryKey: ['task', id, 'comments'], queryFn: () => api.get<CommentDto[]>(`/tasks/${id}/comments`) });
  const [body, setBody] = useState('');
  const add = useMutation({
    mutationFn: () => api.post(`/tasks/${id}/comments`, { body }),
    onSuccess: () => {
      setBody('');
      void qc.invalidateQueries({ queryKey: ['task', id, 'comments'] });
    },
  });
  const err = add.error instanceof ApiError ? add.error : null;
  return (
    <section className="card card-pad">
      <h2>Лента</h2>
      <ul className="feed">
        {comments.data?.map((c) => (
          <FeedItem key={c.id} c={c} />
        ))}
      </ul>
      <form
        className="feed-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (body.trim()) add.mutate();
        }}
      >
        <textarea className="textarea" rows={2} placeholder="Написать комментарий" value={body} onChange={(e) => setBody(e.target.value)} />
        {err && <span className="field-error">{err.message}</span>}
        <button className="btn" disabled={!body.trim() || add.isPending}>
          Отправить
        </button>
      </form>
    </section>
  );
}

function FeedItem({ c }: { c: CommentDto }): ReactNode {
  if (c.kind === 'system') {
    return (
      <li className="feed-system">
        <span className="faint">{formatDate(c.createdAt)}</span> {c.body}
      </li>
    );
  }
  return (
    <li className="feed-comment">
      <Avatar name={c.author?.fullName ?? '?'} size={30} />
      <div>
        <div>
          <b>{c.author?.fullName}</b> <span className="faint">{formatDate(c.createdAt)}</span>
        </div>
        <div className="prose">{c.body}</div>
      </div>
    </li>
  );
}
